import React, {useState, useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {motion} from "framer-motion";
import {
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  User,
  Calendar,
  GraduationCap,
  Target,
  Award,
  TrendingUp,
  Users,
  FileText,
  Brain,
  UserCheck,
} from "lucide-react";

// 🔁 Update path if needed; make sure file name has no spaces.
import BrandLogo from "../Assets/HE.AI-Logo.png";

const COLORS = {
  primary: "#F17C45",
  secondary: "#4365FD",
  background: "#0F172A",
  cardBg: "#1E293B",
  activeBg: "#334155",
  text: "#E2E8F0",
  textSecondary: "#94A3B8",
  accent: "#F59E0B",
  border: "#334155"
};

// IMPORTANT: Accept collapsed and setCollapsed as props
const Sidebar = ({isMobileOpen, setIsMobileOpen, collapsed, setCollapsed}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [children, setChildren] = useState([]);

  // Check if mobile view based on screen width
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (window.innerWidth >= 1024 && isMobileOpen) {
        setIsMobileOpen(false);
      }
      // Reset collapse on mobile
      if (mobile && collapsed) {
        setCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, [isMobileOpen, setIsMobileOpen, collapsed, setCollapsed]);

  // Load children data on component mount
  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const { default: ParentAPI } = await import("../services/ParentAPI");
      const childrenData = await ParentAPI.getChildren();
      setChildren(childrenData);

      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
      }
    } catch (error) {
      console.error("Error loading children:", error);
      setChildren([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/");
  };

  const getIcon = (text) => {
    switch (text) {
      case "Home": return <Home size={20}/>;
      case "Dashboard": return <LayoutDashboard size={20}/>;
      case "Learner's Profile": return <User size={20}/>;
      case "Learner's Classes": return <GraduationCap size={20}/>;
      case "Learner's Teachers": return <Users size={20}/>;
      case "Learner's Activities": return <BookOpen size={20}/>;
      case "Learner's Attendance": return <Calendar size={20}/>;
      case "Learner's Daily Learnings": return <BookOpen size={20}/>;
      case "Learner's Domains": return <Target size={20}/>;
      case "Learner's Competencies": return <Award size={20}/>;
      case "Learner's Holistic Progress": return <TrendingUp size={20}/>;
      case "Parent's Partnership Tracker": return <Users size={20}/>;
      case "Assessment Results": return <FileText size={20}/>;
      case "Official Reports": return <Award size={20}/>;
      case "Annual Academic Records": return <FileText size={20}/>;
      case "Learner's Personalised Learning": return <Brain size={20}/>;
      case "Parent's Profile": return <UserCheck size={20}/>;
      case "Logout": return <LogOut size={20}/>;
      default: return <Home size={20}/>;
    }
  };

  const menuGroups = [
    {
      label: "Overview",
      items: [
        {text: "Home", path: "/HomePage", key: "home"},
        {text: "Dashboard", path: "/ParentDashboard", key: "dashboard"},
      ],
    },
    {
      label: "Learner",
      items: [
        {text: "Learner's Profile", path: "/learner-profile", key: "learner-profile"},
        {text: "Learner's Classes", path: "/learner-classes", key: "learner-classes"},
        {text: "Learner's Teachers", path: "/learner-teachers", key: "learner-teachers"},
        {text: "Learner's Activities", path: "/learner-activities", key: "learner-activities"},
        {text: "Learner's Attendance", path: "/learner-attendance", key: "learner-attendance"},
        {text: "Learner's Competencies", path: "/learner-competencies", key: "learner-competencies"},
      ],
    },
    {
      label: "Official reports",
      items: [
        // Same order as the other panels: Academic, Future Ready (HPC and All Reports follow once published HPC reports exist).
        {text: "Academic Reports", path: "/academic-transcripts", key: "academic-transcripts"},
        {text: "Future Ready Reports", path: "/official-reports", key: "official-reports"},
      ],
    },
    {
      label: "Account",
      items: [
        {text: "Parent's Profile", path: "/parent-profile", key: "parent-profile"},
        {text: "Logout", path: "logout", key: "logout"},
      ],
    },
  ];

  const handleMenuClick = (path, text) => {
    if (text === "Logout") {
      handleLogout();
    } else {
      navigate(path);
      if (isMobile) {
        setIsMobileOpen(false);
      }
    }
  };

  return (
    <div className="h-screen font-[poppins] flex">
      {/* Backdrop for mobile */}
      {isMobile && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav
        className={`
          h-screen text-slate-200 
          transition-all duration-300 ease-in-out
          ${isMobile
            ? `fixed top-0 left-0 z-50 ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} w-64`
            : `relative ${collapsed ? "w-20" : "w-64"}`
          }
          flex flex-col
          border-r border-slate-700
          shadow-2xl shadow-slate-900/50
          font-[poppins]
        `}
        style={{backgroundColor: COLORS.background}}
      >
        {/* Header / Centered Logo + child info */}
        <div
          className={`
            flex flex-col font-[poppins]
            px-4 py-4
            border-b border-slate-700
            bg-gradient-to-r from-slate-800 to-slate-900
          `}
        >
          {/* Logo row */}
          <div className="relative flex items-center justify-center mb-3">
            <button
              onClick={() => navigate("/HomePage")}
              className="block"
              aria-label="Home"
              title="Home"
            >
              <img
                src={BrandLogo}
                alt="HE.AI"
                className={`${(!collapsed || isMobile) ? "h-14 sm:h-16 w-auto" : "h-10 w-10"} object-contain drop-shadow-md`}
                style={{filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"}}
              />
            </button>

            {/* Mobile close button */}
            {isMobile && (
              <button
                onClick={() => setIsMobileOpen(false)}
                className="absolute right-0 text-slate-400 hover:text-red-400 transition-colors"
              >
                <X size={24}/>
              </button>
            )}
          </div>

          {/* Selected Child Display (expanded or mobile) */}
          {selectedChild && (!collapsed || isMobile) && (
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                  <GraduationCap size={16} className="text-white"/>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {selectedChild.name}
                  </p>
                  <p className="text-xs text-slate-300">
                    Grade {selectedChild.grade || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Child initial in collapsed desktop state */}
          {selectedChild && collapsed && !isMobile && (
            <div className="flex justify-center mt-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {selectedChild.name.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <div className="flex-1 px-3 py-4 font-[poppins] overflow-y-auto [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden">
          {menuGroups.map((group, groupIndex) => (
            <section key={group.label} className={groupIndex ? "mt-3" : ""}>
              {(!collapsed || isMobile) && (
                <h2 className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[.14em] text-slate-500">
                  {group.label}
                </h2>
              )}
              <div className="space-y-1">
                {group.items.map((item, index) => {
            const active = location.pathname === item.path;
            const isHovered = hoveredItem === item.key;

            return (
              <div
                key={item.key}
                onClick={() => handleMenuClick(item.path, item.text)}
                onMouseEnter={() => setHoveredItem(item.key)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`
                  group relative flex items-center gap-4 px-3 py-3
                  rounded-xl cursor-pointer transition-all duration-200
                  ${active
                    ? "bg-gradient-to-r from-orange-500/20 to-orange-600/20 border border-orange-500/30"
                    : isHovered
                      ? "bg-slate-800/60 border border-slate-600/50"
                      : "border border-transparent hover:bg-slate-800/40"
                  }
                  transform hover:scale-105 hover:shadow-lg hover:shadow-orange-500/10
                `}
                style={{animationDelay: `${index * 0.05}s`}}
              >
                {/* Active indicator */}
                {active && (
                  <div
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 rounded-r-full"
                    style={{backgroundColor: COLORS.primary}}
                  />
                )}

                {/* Icon */}
                <div
                  className={`
                    flex items-center justify-center w-8 h-8
                    ${active ? "text-orange-400" : isHovered ? "text-blue-400" : "text-slate-400"}
                    transition-colors duration-200
                  `}
                >
                  {getIcon(item.text)}
                </div>

                {/* Text */}
                {(!collapsed || isMobile) && (
                  <span
                    className={`
                      text-sm font-medium transition-colors duration-200
                      ${active ? "text-orange-300" : isHovered ? "text-blue-300" : "text-slate-300"}
                    `}
                  >
                    {item.text}
                  </span>
                )}

                {/* Tooltip for collapsed desktop state */}
                {collapsed && !isMobile && (
                  <div
                    className={`
                      absolute left-full ml-3 px-3 py-2 bg-slate-800 text-white text-sm
                      rounded-lg shadow-lg opacity-0 group-hover:opacity-100
                      transition-all duration-200 pointer-events-none z-50
                      border border-slate-600
                      ${isHovered ? "translate-x-0" : "translate-x-2"}
                    `}
                  >
                    {item.text}
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-slate-800 rotate-45 border-l border-b border-slate-600"/>
                  </div>
                )}
              </div>
            );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Collapse Toggle Button - Only show on desktop */}
        {!isMobile && (
          <div className="border-t border-slate-700 p-4">
            <motion.button
              onClick={() => {
                if (typeof setCollapsed === "function") {
                  setCollapsed(!collapsed);
                } else {
                  console.error("setCollapsed is not a function");
                }
              }}
              className={`
                w-full flex items-center justify-center gap-3 px-4 py-3
                bg-gradient-to-r from-slate-800 to-slate-700
                hover:from-slate-700 hover:to-slate-600
                border border-slate-600 hover:border-slate-500
                rounded-xl transition-all duration-200
                text-slate-300 hover:text-orange-300
                shadow-lg hover:shadow-xl
                transform hover:scale-105
                group
              `}
              whileTap={{scale: 0.87}}
            >
              <div className="flex items-center justify-center">
                {collapsed ? (
                  <ChevronRight size={18} className="group-hover:text-orange-400 transition-colors"/>
                ) : (
                  <>
                    <ChevronLeft size={18} className="group-hover:text-orange-400 transition-colors"/>
                    <span className="text-sm font-medium ml-2">Collapse</span>
                  </>
                )}
              </div>
            </motion.button>
          </div>
        )}

        {/* Gradient overlay for visual depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/20 pointer-events-none"/>
      </nav>

      {/* Mobile menu toggle button */}
      {isMobile && !isMobileOpen && (
        <button
          className="fixed top-6 left-6 z-40 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border border-slate-600"
          style={{background: COLORS.background, color: COLORS.primary}}
          onClick={() => setIsMobileOpen(true)}
        >
          <Menu size={24}/>
        </button>
      )}
    </div>
  );
};

export default Sidebar;
